/**
 * Census Data service for demographic analysis
 */

import { apiRequest } from './api';

export interface DemographicsData {
  total_population: number;
  median_age: number;
  median_household_income: number;
  population_by_age: {
    under_5: number;
    age_5_to_9: number;
    age_10_to_14: number;
    age_15_to_17: number;
    age_18_to_19: number;
    age_20_to_24: number;
    age_25_to_29: number;
    age_30_to_34: number;
    age_35_to_39: number;
    age_40_to_44: number;
    age_45_to_49: number;
    age_50_to_54: number;
    age_55_to_59: number;
    age_60_to_64: number;
    age_65_to_69: number;
    age_70_to_74: number;
    age_75_to_79: number;
    age_80_to_84: number;
    age_85_and_over: number;
  };
  population_by_race: {
    white: number;
    black: number;
    american_indian: number;
    asian: number;
    pacific_islander: number;
    other: number;
    two_or_more: number;
  };
  education_level: {
    less_than_high_school: number;
    high_school: number;
    some_college: number;
    associates: number;
    bachelors: number;
    graduate: number;
  };
  housing_units: number;
  owner_occupied_housing: number;
  renter_occupied_housing: number;
}

/**
 * Fetch demographics data for a location
 */
export const fetchDemographicsData = async (lat: number, lng: number): Promise<DemographicsData> => {
  try {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString()
    });

    const response = await apiRequest(`/external/census/demographics?${params.toString()}`, 'GET') as DemographicsData;
    return response;
  } catch (error) {
    console.error('Error fetching demographics data:', error);
    throw error;
  }
};

/**
 * Fetch raw census data for a location
 */
export const fetchRawCensusData = async (
  lat: number, 
  lng: number, 
  variables: string, 
  year: string = '2022', 
  dataset: string = 'acs/acs5'
): Promise<Record<string, any>> => {
  try {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
      variables,
      year,
      dataset
    });

    const response = await apiRequest(`/external/census/raw?${params.toString()}`, 'GET') as Record<string, any>;
    return response;
  } catch (error) {
    console.error('Error fetching raw census data:', error);
    throw error;
  }
};

/**
 * Format population numbers for display
 */
export const formatPopulation = (num: number): string => {
  return new Intl.NumberFormat().format(num);
};

/**
 * Format currency for display
 */
export const formatCurrency = (num: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

/**
 * Format percentage for display
 */
export const formatPercentage = (numerator: number, denominator: number): string => {
  if (denominator === 0) return '0.0%';
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
};

/**
 * Calculate age distribution percentages
 */
export const calculateAgeDistribution = (populationByAge: DemographicsData['population_by_age'], totalPopulation: number) => {
  const ageGroups = [
    { label: 'Under 18', value: populationByAge.under_5 + populationByAge.age_5_to_9 + populationByAge.age_10_to_14 + populationByAge.age_15_to_17 },
    { label: '18-34', value: populationByAge.age_18_to_19 + populationByAge.age_20_to_24 + populationByAge.age_25_to_29 + populationByAge.age_30_to_34 },
    { label: '35-54', value: populationByAge.age_35_to_39 + populationByAge.age_40_to_44 + populationByAge.age_45_to_49 + populationByAge.age_50_to_54 },
    { label: '55-64', value: populationByAge.age_55_to_59 + populationByAge.age_60_to_64 },
    { label: '65+', value: populationByAge.age_65_to_69 + populationByAge.age_70_to_74 + populationByAge.age_75_to_79 + populationByAge.age_80_to_84 + populationByAge.age_85_and_over }
  ];

  return ageGroups.map(group => ({
    ...group,
    percentage: formatPercentage(group.value, totalPopulation)
  }));
};

/**
 * Calculate race distribution percentages
 */
export const calculateRaceDistribution = (populationByRace: DemographicsData['population_by_race'], totalPopulation: number) => {
  const raceGroups = [
    { label: 'White', value: populationByRace.white },
    { label: 'Black/African American', value: populationByRace.black },
    { label: 'Asian', value: populationByRace.asian },
    { label: 'American Indian', value: populationByRace.american_indian },
    { label: 'Pacific Islander', value: populationByRace.pacific_islander },
    { label: 'Other', value: populationByRace.other },
    { label: 'Two or More Races', value: populationByRace.two_or_more }
  ];

  return raceGroups.map(group => ({
    ...group,
    percentage: formatPercentage(group.value, totalPopulation)
  }));
};

/**
 * Calculate education distribution percentages
 */
export const calculateEducationDistribution = (educationLevel: DemographicsData['education_level']) => {
  const totalEducation = Object.values(educationLevel).reduce((sum: number, value: number) => sum + value, 0);
  
  const educationGroups = [
    { label: 'Less than High School', value: educationLevel.less_than_high_school },
    { label: 'High School', value: educationLevel.high_school },
    { label: 'Some College', value: educationLevel.some_college },
    { label: 'Associates Degree', value: educationLevel.associates },
    { label: 'Bachelors Degree', value: educationLevel.bachelors },
    { label: 'Graduate Degree', value: educationLevel.graduate }
  ];

  return educationGroups.map(group => ({
    ...group,
    percentage: formatPercentage(group.value, totalEducation)
  }));
};

/**
 * Calculate housing distribution percentages
 */
export const calculateHousingDistribution = (ownerOccupied: number, renterOccupied: number) => {
  const totalOccupied = ownerOccupied + renterOccupied;
  
  return {
    ownerOccupied: {
      value: ownerOccupied,
      percentage: formatPercentage(ownerOccupied, totalOccupied)
    },
    renterOccupied: {
      value: renterOccupied,
      percentage: formatPercentage(renterOccupied, totalOccupied)
    }
  };
};
